/**
 * Frases que fazem parte do guia (títulos automáticos, blocos de texto, título por omissão,
 * exportações). Vão na língua do GUIA, não na de quem o está a ver: um guia gravado em
 * português continua em português quando é partilhado.
 */
import { catalog, DEFAULT_LOCALE, isLocale, plural, type Locale } from '@nolens/i18n';
import type { Guide, TextTone } from './steps';

export type { Locale } from '@nolens/i18n';

/**
 * Língua de um guia. Os guias anteriores ao campo `language` foram todos gravados em
 * português; os novos têm sempre a língua de quem os gravou.
 */
export function guideLocale(guide: Pick<Guide, 'language'> | null | undefined): Locale {
  return isLocale(guide?.language) ? guide!.language! : 'pt-PT';
}

export const GUIDE_LOCALE_FALLBACK: Locale = DEFAULT_LOCALE;

export const guideText = catalog(
  {
    // Títulos automáticos (os valores já vêm entre aspas, na forma da língua)
    open: (url: string) => `Open ${url}`,
    openPage: 'Open the page',
    switchTab: (tab: string) => `Switch to the ${tab} tab`,
    nextTab: 'next',
    dblclick: (label: string) => `Double-click ${label}`,
    dblclickHere: 'Double-click here',
    type: (typed: string, field: string | null) => `Type ${typed}` + (field ? ` in ${field}` : ''),
    fill: (field: string) => `Fill in ${field}`,
    fillField: 'Fill in the field',
    pressEnter: (field: string | null) => 'Press Enter' + (field ? ` in ${field}` : ''),
    select: (option: string, field: string | null) => `Select ${option}` + (field ? ` in ${field}` : ''),
    selectAny: (field: string | null) => 'Select an option' + (field ? ` in ${field}` : ''),
    click: (label: string) => `Click ${label}`,
    clickField: 'Click the field',
    clickHere: 'Click here',
    // Só na app de Mac
    rightclick: (label: string) => `Right-click ${label}`,
    rightclickHere: 'Right-click here',
    switchApp: (app: string) => `Switch to the ${app} app`,
    switchOtherApp: 'Switch to the next app',
    switchWindow: (win: string) => `Switch to the ${win} window`,
    choose: (path: string) => `Choose ${path}`,
    chooseMenu: 'Choose an option from the menu',
    pressShortcut: (keys: string) => `Press ${keys}`,
    pressKeys: 'Press the keyboard shortcut',
    spaceKey: 'Space',
    // Blocos de texto
    tip: 'Tip',
    alert: 'Warning',
    info: 'Note',
    heading: 'Section',
    // Título por omissão
    howToUse: (app: string) => `How to use ${app}`,
    guideOf: (date: string) => `Guide from ${date}`,
    // Exportações
    guide: 'Guide',
    step: 'Step',
    steps: (n: number) => n + ' ' + plural('en', n, { one: 'step', other: 'steps' }),
    stepsWord: (n: number) => plural('en', n, { one: 'Step', other: 'Steps' }),
    aboutMin: (n: number) => `about ${n} min`,
    contents: 'Contents',
    endOfGuide: 'End of guide',
    madeWith: 'Made with Nolens Guides',
  },
  {
    'pt-PT': {
      open: (url) => `Abra ${url}`,
      openPage: 'Abra a página',
      switchTab: (tab) => `Mude para o separador ${tab}`,
      nextTab: 'seguinte',
      dblclick: (label) => `Faça duplo clique em ${label}`,
      dblclickHere: 'Faça duplo clique aqui',
      type: (typed, field) => `Escreva ${typed}` + (field ? ` em ${field}` : ''),
      fill: (field) => `Preencha ${field}`,
      fillField: 'Preencha o campo',
      pressEnter: (field) => 'Prima Enter' + (field ? ` em ${field}` : ''),
      select: (option, field) => `Escolha ${option}` + (field ? ` em ${field}` : ''),
      selectAny: (field) => 'Escolha uma opção' + (field ? ` em ${field}` : ''),
      click: (label) => `Clique em ${label}`,
      clickField: 'Clique no campo',
      clickHere: 'Clique aqui',
      rightclick: (label) => `Clique com o botão direito em ${label}`,
      rightclickHere: 'Clique aqui com o botão direito',
      switchApp: (app) => `Mude para a app ${app}`,
      switchOtherApp: 'Mude para a app seguinte',
      switchWindow: (win) => `Mude para a janela ${win}`,
      choose: (path) => `Escolha ${path}`,
      chooseMenu: 'Escolha uma opção do menu',
      pressShortcut: (keys) => `Prima ${keys}`,
      pressKeys: 'Prima o atalho de teclado',
      spaceKey: 'Espaço',
      tip: 'Dica',
      alert: 'Atenção',
      info: 'Nota',
      heading: 'Secção',
      howToUse: (app) => `Como usar ${app}`,
      guideOf: (date) => `Guia de ${date}`,
      guide: 'Guia',
      step: 'Passo',
      steps: (n) => n + ' ' + plural('pt-PT', n, { one: 'passo', other: 'passos' }),
      stepsWord: (n) => plural('pt-PT', n, { one: 'Passo', other: 'Passos' }),
      aboutMin: (n) => `cerca de ${n} min`,
      contents: 'Sumário',
      endOfGuide: 'Fim do guia',
      madeWith: 'Feito com Nolens Guides',
    },
  },
);

/** Nome de um tipo de bloco de texto (Dica, Atenção, Nota, Secção) na língua do guia. */
export function textToneLabel(tone: TextTone, locale: Locale): string {
  return guideText(locale)[tone];
}
